import React, { useMemo, useRef, useState, type ReactNode } from 'react'
import {
  Button,
  colors,
  fontSize,
  NotificationLevel,
  notify,
  spacing,
  Text,
} from '@retailer-platform/shared-components'
import styled from '@emotion/styled'
import faker from 'faker'
import { IconButton } from '@instacart/ids-customers'
import {
  ArrowRightIcon,
  ChevronRightIcon,
  EditIcon,
  TrashIcon,
  ArrowUpIcon,
} from '@instacart/ids-core'
import { Link } from '@retailer-platform/shared-components/src/tds'
import { useGetStepVersionQuery, useGetListStepVersionsQuery } from '../../../api'
import { useDomainMessages } from '../../../utils/domain/intl'
import {
  type AttributeManagementCategoryEnum,
  type AttributeManagementStepVersion,
  graphQLStepVersionToAttributeManagementStepVersion,
  type PipelineStep,
} from '../../../types/attributeManagement.types'
import { useDomainScopedPathname } from '../../../utils/domain/routing'
import PipelineFormStepSelectModal from './PipelineFormStepSelectModal'

interface Props {
  category?: AttributeManagementCategoryEnum | null
  step: PipelineStep | null
  onChange: (step: PipelineStep) => void
  onDelete: () => void
  disabled: boolean
  headerOverride?: ReactNode
  contentOverride?: ReactNode
}

const TileDiv = styled.div({
  verticalAlign: 'middle',
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${colors.GRAYSCALE.X80}`,
  backgroundColor: colors.GRAYSCALE.X10,
})

const TileHeader = styled.div({
  display: 'flex',
  flexDirection: 'column',
  borderBottom: `1px solid ${colors.GRAYSCALE.X80}`,
  padding: spacing.X8,
  alignItems: 'center',
  whiteSpace: 'nowrap',
})

const TileBody = styled.div({
  padding: spacing.X16,
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.X8,
  alignItems: 'center',
  whiteSpace: 'nowrap',
})

const OverrideGrid = styled.div({
  display: 'grid',
  gridTemplateColumns: '1fr auto auto auto',
  gap: spacing.X8,
})

const TextRight = styled.div({
  textAlign: 'right',
})

const PipelineFormStepTile: React.FC<Props> = ({
  category,
  step,
  onChange,
  onDelete,
  disabled,
  headerOverride,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const i18n = useDomainMessages({
    errorMessage:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.errorMessage',
    deleteStep:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.deleteStep',
    editStep:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.editStep',
  })
  const idempotencyKey = useRef(faker.random.uuid())
  const idemKey = useMemo(
    () => idempotencyKey.current + step?.stepId + step?.version,
    [idempotencyKey, step?.stepId, step?.version]
  )
  const { data } = useGetStepVersionQuery({
    variables: {
      stepId: step?.stepId || '',
      version: step?.version || '',
      clientId: 'catalog_camp_ipp',
      idempotencyKey: idemKey,
    },
    skip: !step?.stepId || !step?.version,
    onError: () => {
      notify({
        level: NotificationLevel.Error,
        contents: i18n.errorMessage,
      })
    },
  })
  const stepVersionDetails = React.useMemo(() => {
    const stepVersion = data?.instacart_catalog_catflow_v1_CatflowAPI_GetStepVersion?.stepVersion
    if (!stepVersion) return null
    return {
      ...stepVersion,
      attributeOverrides: step?.attributeOverrides || {},
    }
  }, [data, step])

  const stepVersionCategory = React.useMemo(
    () => stepVersionDetails?.step?.stepCategory,
    [stepVersionDetails]
  )
  const openModal = () => setIsModalOpen(true)
  return (
    <>
      <Tile
        header={
          headerOverride || (
            <Header
              type={category || stepVersionCategory || 'Step'}
              name={stepVersionDetails?.step?.name}
              id={step?.stepId}
              version={stepVersionDetails?.version}
            />
          )
        }
        content={
          stepVersionDetails ? (
            <StepBody
              step={stepVersionDetails}
              disableActions={disabled}
              onDelete={onDelete}
              openModal={openModal}
              onChange={onChange}
            />
          ) : (
            <ButtonTileBody
              buttonCategory={category || null}
              onClick={openModal}
              disabled={disabled}
            />
          )
        }
      />
      {isModalOpen && (
        <PipelineFormStepSelectModal
          category={category || null}
          onClose={() => {
            setIsModalOpen(false)
          }}
          onConfirm={step => {
            onChange(step)
            setIsModalOpen(false)
          }}
        />
      )}
    </>
  )
}

interface JoinChevronButtonProps {
  onAdd: (step: PipelineStep) => void
  disabled: boolean
}

export const JoinChevronButton: React.FC<JoinChevronButtonProps> = ({ onAdd, disabled }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const i18n = useDomainMessages({
    addStep:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.addStep',
  })

  const openModal = () => setIsModalOpen(true)
  return (
    <>
      <IconButton
        disabled={disabled}
        icon={ChevronRightIcon}
        accessibleLabel={i18n.addStep}
        onPress={openModal}
      />
      {isModalOpen && (
        <PipelineFormStepSelectModal
          category={null}
          onClose={() => {
            setIsModalOpen(false)
          }}
          onConfirm={step => {
            onAdd(step)
            setIsModalOpen(false)
          }}
        />
      )}
    </>
  )
}

interface TileProps {
  header: ReactNode
  content: ReactNode
}

const Tile = ({ header, content }: TileProps) => (
  <TileDiv>
    <TileHeader>{header}</TileHeader>
    <TileBody>{content}</TileBody>
  </TileDiv>
)

interface StepBodyProps {
  step: any | null
  disableActions: boolean
  onDelete: () => void
  openModal: () => void
  onChange: (step: PipelineStep) => void
}

const StepBody = ({ step, disableActions, onDelete, openModal, onChange }: StepBodyProps) => {
  const i18n = useDomainMessages({
    deleteStep:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.deleteStep',
    editStep:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.editStep',
    updateToLatestVersion:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepTile.updateToLatestVersion',
    updateToLatestVersionSuccess:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepTile.updateToLatestVersionSuccess',
    errorMessage:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.errorMessage',
  })

  // Get all versions for this step
  const idempotencyKey = useRef(faker.random.uuid())
  const { data: stepVersionsData } = useGetListStepVersionsQuery({
    variables: {
      stepId: step.stepId,
      clientId: 'catalog_camp_ipp',
      idempotencyKey: idempotencyKey.current,
    },
    onError: () => {
      notify({
        level: NotificationLevel.Error,
        contents: i18n.errorMessage,
      })
    },
  })

  const availableStepVersions: AttributeManagementStepVersion[] = useMemo(
    () =>
      stepVersionsData?.instacart_catalog_catflow_v1_CatflowAPI_ListStepVersions?.stepVersions
        ?.filter(v => !v.tombstoned)
        .map(item => graphQLStepVersionToAttributeManagementStepVersion(item))
        ?.sort((a, b) => parseInt(b.version ?? '0') - parseInt(a.version ?? '0')) || [],
    [stepVersionsData]
  )

  const latestStepVersion = useMemo(() => availableStepVersions[0], [availableStepVersions])
  const isCurrentVersionLatest = useMemo(
    () => latestStepVersion?.version === step?.version,
    [latestStepVersion?.version, step?.version]
  )

  const handleUpdateToLatestVersion = () => {
    if (!latestStepVersion?.version) return

    onChange({
      stepId: step.stepId,
      stepVersionId: latestStepVersion.id,
      version: latestStepVersion.version,
      attributeOverrides: step.attributeOverrides || {},
    })
    notify({
      level: NotificationLevel.Success,
      contents: i18n.updateToLatestVersionSuccess,
    })
  }

  return (
    <>
      <Text size="small" weight="bold">
        Overrides
      </Text>
      {Object.keys(step?.attributeOverrides || {}).length === 0 ? (
        <Text size="small" weight="semibold">
          No overrides
        </Text>
      ) : (
        <OverrideGrid>
          {Object.keys(step?.attributeOverrides || {}).map(key => (
            <React.Fragment key={key}>
              <TextRight>
                <Text size="small" weight="semibold">
                  {key}:
                </Text>
              </TextRight>
              <TextRight>{step?.attributes[key] || 'null'}</TextRight>
              <ArrowRightIcon size={fontSize.X14} />
              <div>{step?.attributeOverrides[key] || 'null'}</div>
            </React.Fragment>
          ))}
        </OverrideGrid>
      )}
      <div>
        <IconButton
          disabled={disableActions}
          icon={TrashIcon}
          accessibleLabel={i18n.deleteStep}
          onPress={() => {
            onDelete()
          }}
        />
        <IconButton
          disabled={disableActions}
          icon={EditIcon}
          accessibleLabel={i18n.editStep}
          onPress={() => {
            openModal()
          }}
        />
        <IconButton
          disabled={disableActions || isCurrentVersionLatest || !latestStepVersion}
          icon={ArrowUpIcon}
          accessibleLabel={i18n.updateToLatestVersion}
          onPress={handleUpdateToLatestVersion}
        />
      </div>
    </>
  )
}

interface ButtonTileBodyProps {
  buttonCategory: string | null
  onClick: () => void
  disabled: boolean
}

const ButtonTileBody = ({ buttonCategory, onClick, disabled }: ButtonTileBodyProps) => (
  <Button variant="primary" onClick={onClick} disabled={disabled}>
    Add {buttonCategory} Step
  </Button>
)

interface HeaderProps {
  type: string
  name: string | null | undefined
  version: string | null | undefined
  id: string | null | undefined
}

const Header = ({ type, name, version, id }: HeaderProps) => {
  const basePath = useDomainScopedPathname('app-admin-catalog-admin-attribute-management-details')
  const stepUrl = id ? `${basePath}?stepId=${id}` : null

  return (
    <>
      <Text weight="bold">{type}</Text>
      {stepUrl ? (
        <Link href={stepUrl} target="_blank" rel="noopener noreferrer">
          {name}
        </Link>
      ) : (
        <Text weight="semibold">{name}</Text>
      )}
      {version && (
        <Text size="small" weight="semibold">
          Version {version}
        </Text>
      )}
    </>
  )
}

interface EndTileProps {
  onAdd: (step: PipelineStep) => void
}

export const EndTile = ({ onAdd }: EndTileProps) => (
  <PipelineFormStepTile
    category={null}
    step={null}
    onChange={onAdd}
    onDelete={() => {}}
    disabled={false}
    headerOverride={<Text weight="bold">End</Text>}
  />
)

export default PipelineFormStepTile
